import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { labelHobby } from "@/lib/constants";
import { Copy, Check, Share2, Users, ChevronRight, Camera, Trash2, AlertTriangle, X } from "lucide-react";
import Avatar from "@/components/Avatar";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [referral, setReferral] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ bio: "", hobbies: [], occupation: "", region: "" });
  const [options, setOptions] = useState({ hobbies: [], regions: [] });
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const avatarRef = useRef(null);

  // Delete-account 2-step confirm modal state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteStage, setDeleteStage] = useState(1);           // 1: warn, 2: type-to-confirm
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  useEffect(() => {
    api.get("/users/me/referral").then((r) => setReferral(r.data));
    api.get("/meta/options").then((r) => setOptions(r.data));
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        bio: user.bio || "",
        hobbies: user.hobbies || [],
        occupation: user.occupation || "",
        region: user.region || "",
      });
    }
  }, [user]);

  if (!user) return null;

  const copyCode = async () => {
    if (!referral) return;
    try {
      await navigator.clipboard.writeText(referral.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  const shareInvite = async () => {
    if (!referral) return;
    const url = `${window.location.origin}/apply`;
    const text = `You're invited to Lumière Club. Use my referral code: ${referral.referral_code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Lumière Club invitation", text, url });
        return;
      } catch (e) { /* ignore */ }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      alert("Invitation copied to clipboard");
    } catch {
      /* ignore */
    }
  };

  const toggle = (h) =>
    setForm((s) => ({ ...s, hobbies: s.hobbies.includes(h) ? s.hobbies.filter((x) => x !== h) : [...s.hobbies, h] }));

  const save = async () => {
    setErr("");
    try {
      await api.patch("/users/me", form);
      await refresh();
      setEditing(false);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 fade-up">
      {/* Header */}
      <div className="flex items-start gap-5">
        <div className="relative">
          <button
            type="button"
            onClick={() => avatarRef.current?.click()}
            className="relative group"
            data-testid="profile-avatar-button"
            title="Change avatar"
          >
            <Avatar path={user.avatar_path} name={user.name} size={88} className="ring-2 ring-[#d4af37]/30" />
            <span className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <Camera size={20} strokeWidth={1.5} className="text-[#d4af37]" />
            </span>
            {uploading && (
              <span className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center text-[10px] uppercase tracking-widest text-[#d4af37]">
                …
              </span>
            )}
          </button>
          <input
            ref={avatarRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="profile-avatar-input"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setErr("");
              setUploading(true);
              try {
                const fd = new FormData();
                fd.append("file", f);
                const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                await api.patch("/users/me", { avatar_path: up.data.path });
                await refresh();
              } catch (ex) {
                setErr(formatApiError(ex));
              } finally {
                setUploading(false);
                e.target.value = "";
              }
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">Member · 会员</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1 truncate">{user.name}</h1>
          <p className="text-white/50 mt-1">
            {user.occupation} · {user.region}
          </p>
        </div>
      </div>

      {/* Referral */}
      <div className="border border-[#d4af37]/30 bg-[#d4af37]/5 rounded-lg p-5" data-testid="profile-referral-card">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Your Referral Code</div>
        <div className="flex items-center justify-between mt-3">
          <div className="font-mono text-3xl tracking-[0.4em] text-white" data-testid="referral-code">
            {referral?.referral_code || "————"}
          </div>
          <div className="flex gap-2">
            <button onClick={copyCode} className="p-2 text-white/70 hover:text-[#d4af37]" data-testid="copy-referral" aria-label="Copy referral code" title="Copy">
              {copied ? <Check size={18} strokeWidth={1.5} className="text-[#d4af37]" /> : <Copy size={18} strokeWidth={1.5} />}
            </button>
            <button onClick={shareInvite} className="p-2 text-white/70 hover:text-[#d4af37]" data-testid="share-referral" aria-label="Share referral code" title="Share">
              <Share2 size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <p className="text-xs text-white/50 mt-3">
          You have referred {referral?.referred_count ?? 0} member{(referral?.referred_count ?? 0) === 1 ? "" : "s"}.
        </p>
      </div>

      {/* Hobbies */}
      <section data-testid="profile-hobbies-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-2xl">Interests</h2>
          {!editing && (
            <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest text-[#d4af37]" data-testid="profile-edit">
              Edit
            </button>
          )}
        </div>
        {!editing ? (
          <div className="flex flex-wrap gap-2">
            {(user.hobbies || []).map((h) => (
              <span key={h} className="bg-[#1a1a1a] border border-white/10 text-white/80 px-3 py-1.5 rounded-full text-sm">
                {labelHobby(h)}
              </span>
            ))}
            {(!user.hobbies || user.hobbies.length === 0) && <span className="text-white/40">No interests added.</span>}
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">Bio</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={3}
                className="editorial-input mt-2"
                data-testid="profile-bio-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">Hobbies</label>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-hobbies-edit">
                {options.hobbies.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className="hobby-chip rounded-full bg-[#1a1a1a] border border-white/10 text-white/80 px-4 py-2 text-sm"
                    data-selected={form.hobbies.includes(h)}
                    onClick={() => toggle(h)}
                    data-testid={`profile-hobby-${h}`}
                  >
                    {labelHobby(h)}
                  </button>
                ))}
              </div>
            </div>
            {err && <p className="text-red-400 text-sm" data-testid="profile-error">{err}</p>}
            <div className="flex gap-3">
              <button onClick={save} className="bg-[#d4af37] text-black px-6 py-3 text-sm uppercase tracking-widest hover:bg-[#f3e5ab]" data-testid="profile-save">
                Save
              </button>
              <button onClick={() => setEditing(false)} className="text-white/60 px-6 py-3 text-sm uppercase tracking-widest" data-testid="profile-cancel">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {user.bio && !editing && (
        <section>
          <h2 className="font-serif text-2xl mb-3">About</h2>
          <p className="text-white/80 leading-relaxed">{user.bio}</p>
        </section>
      )}

      <section className="border-t border-white/10 pt-6 text-sm text-white/40 space-y-1">
        <div>Email: {user.email}</div>
        <div>Phone: {user.phone || "—"}</div>
        <div>Status: <span className="text-[#d4af37] uppercase tracking-widest">{user.status}</span></div>
      </section>

      <Link
        to="/friends"
        className="flex items-center justify-between border border-white/10 hover:border-[#d4af37]/40 rounded-md p-4 bg-[#141414] transition"
        data-testid="profile-friends-link"
      >
        <div className="flex items-center gap-3">
          <Users size={18} strokeWidth={1.5} className="text-[#d4af37]" />
          <div>
            <div className="text-sm font-medium">Your circle</div>
            <div className="text-xs text-white/50">Friends, requests & search</div>
          </div>
        </div>
        <ChevronRight size={16} strokeWidth={1.5} className="text-white/40" />
      </Link>

      {/* Danger zone — delete account (Apple 5.1.1(v) + PDPA right-to-erasure) */}
      <section
        className="mt-8 border border-red-500/25 rounded-md p-5 bg-red-950/10"
        data-testid="profile-danger-zone"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.5} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-red-200 text-sm font-medium">
              Delete my account · 删除我的账户
            </h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              This will permanently delete your profile, stories, group posts, direct messages,
              friend connections, notifications and push subscriptions from Lumière Club. This
              action cannot be undone.
            </p>
            <p className="text-[11px] text-white/40 mt-1 leading-relaxed">
              永久删除您的个人资料、动态、群组帖子、私信、好友关系、通知与推送订阅。此操作不可撤销。
            </p>
            <button
              type="button"
              onClick={() => {
                setDeleteOpen(true);
                setDeleteStage(1);
                setDeleteConfirmText("");
                setDeleteErr("");
              }}
              className="mt-4 inline-flex items-center gap-2 border border-red-500/40 text-red-200 hover:bg-red-500/10 px-3 py-2 text-xs uppercase tracking-widest transition"
              data-testid="profile-delete-account-open"
            >
              <Trash2 size={14} strokeWidth={1.5} />
              Delete my account
            </button>
          </div>
        </div>
      </section>

      {deleteOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm fade-up"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
          data-testid="profile-delete-account-modal"
        >
          <div className="w-full max-w-md bg-[#0a0a0a] border border-red-500/40 rounded-lg shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-red-200">
                <AlertTriangle size={16} strokeWidth={1.5} />
                <h3 id="delete-account-title" className="text-sm uppercase tracking-[0.2em]">
                  {deleteStage === 1 ? "Are you sure?" : "Final confirmation"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteSubmitting}
                className="text-white/60 hover:text-white disabled:opacity-40"
                aria-label="Close"
                data-testid="profile-delete-account-close"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            {deleteStage === 1 && (
              <div className="p-5 space-y-3 text-sm text-white/80">
                <p>
                  Deleting your account is <span className="text-red-300 font-medium">permanent</span>.
                  All of the following will be removed immediately:
                </p>
                <ul className="text-xs text-white/60 list-disc pl-5 space-y-1">
                  <li>Profile, avatar, bio, hobbies, referral history</li>
                  <li>Every story, group post, comment and like you&apos;ve made</li>
                  <li>All direct messages (in both directions)</li>
                  <li>Friend connections and pending requests</li>
                  <li>Notifications and push subscriptions</li>
                </ul>
                <p className="text-xs text-white/50 pt-1">
                  If you&apos;d like a copy of your data first, email{" "}
                  <span className="text-[#d4af37]">concierge@lumiere.club</span> before continuing.
                </p>
                <div className="flex items-center justify-end gap-2 pt-3">
                  <button
                    type="button"
                    onClick={() => setDeleteOpen(false)}
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-white/15 hover:border-white/30"
                    data-testid="profile-delete-account-cancel-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteStage(2)}
                    className="px-4 py-2 text-xs uppercase tracking-widest bg-red-600 text-white hover:bg-red-500"
                    data-testid="profile-delete-account-continue"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {deleteStage === 2 && (
              <div className="p-5 space-y-3 text-sm text-white/80">
                <p>
                  To confirm, please type{" "}
                  <span className="font-mono text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded">
                    delete my account
                  </span>{" "}
                  below.
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete my account"
                  autoFocus
                  disabled={deleteSubmitting}
                  className="w-full bg-transparent border border-white/15 focus:border-red-400 outline-none px-3 py-2 rounded font-mono text-sm disabled:opacity-40"
                  data-testid="profile-delete-account-confirm-input"
                />
                {deleteErr && (
                  <p className="text-xs text-red-300" data-testid="profile-delete-account-error">
                    {deleteErr}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteStage(1);
                      setDeleteErr("");
                    }}
                    disabled={deleteSubmitting}
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-white/15 hover:border-white/30 disabled:opacity-40"
                    data-testid="profile-delete-account-back"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (deleteConfirmText.trim().toLowerCase() !== "delete my account") {
                        setDeleteErr("Please type the phrase exactly to confirm.");
                        return;
                      }
                      setDeleteErr("");
                      setDeleteSubmitting(true);
                      try {
                        await api.delete("/auth/me");
                        setDeleteOpen(false);
                        await logout();
                        nav("/", { replace: true });
                      } catch (e2) {
                        setDeleteErr(formatApiError(e2));
                      } finally {
                        setDeleteSubmitting(false);
                      }
                    }}
                    disabled={
                      deleteSubmitting ||
                      deleteConfirmText.trim().toLowerCase() !== "delete my account"
                    }
                    className="px-4 py-2 text-xs uppercase tracking-widest bg-red-600 text-white hover:bg-red-500 disabled:opacity-30 flex items-center gap-2"
                    data-testid="profile-delete-account-confirm"
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                    {deleteSubmitting ? "Deleting…" : "Delete permanently"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
